/**
 * Conversation Store — Shared Helpers
 *
 * Utility functions extracted from the monolithic conversationStore.
 * Used by individual slices to avoid duplication.
 */

import { AccessibilityInfo } from "react-native";
import type { Socket } from "socket.io-client";
import { getSocket } from "../services/socket";
import { onServerEvent, offServerEvent, emitEvent } from "../services/socketValidation";
import { api } from "../services/api";
import { createLogger } from "../utils/logger";
import { createAnalysisError } from "../utils/conversationErrors";
import { useStreamingTTSStore } from "./streamingTTSStore";
import i18n from "../i18n";
import {
  SCHEMA_VERSION,
  type TokenPayload,
  type MessageSavedPayload,
  type SendMessageAck,
  type SendMessageErrorPayload,
  type StartSessionErrorPayload,
  type RegenerateMessageAck,
  type RegenerateMessageErrorPayload,
  type SendAudioErrorPayload,
  type AudioErrorPayload,
  type TranscriptionErrorPayload,
  type AnalysisCompletePayload,
  type AnalysisFailedPayload,
  type StreamCompletePayload,
  type SessionTransitionPayload,
} from "../generated/api/socket-events";

/**
 * Cross-cutting "stream error" shape the UI layer reasons about. Every
 * op-specific async error event (send_message_error, start_session_error,
 * regenerate_message_error, send_audio_error) uses the same
 * {code, message, category, correlationId?} shape — only the narrow
 * `code` union differs per op. At the UI layer we collapse to the
 * broader union since listeners translate the code to an i18n key.
 */
type StreamErrorPayload =
  | SendMessageErrorPayload
  | StartSessionErrorPayload
  | RegenerateMessageErrorPayload
  | SendAudioErrorPayload;
import {
  AnalyzedMessage,
  PronunciationResult,
  MessageStatus,
  ErrorInfo,
  DIRECTIVE_TOOL_NAMES,
} from "../types/conversation";
import type { ChatDirectives, ConversationAnalysis } from "../types/conversation";
import { AI_OPENING_TIMEOUT_MS, ANALYSIS_LISTENER_TIMEOUT_MS, ANALYSIS_STALL_TIMEOUT_MS, CONVERSATION_TIMEOUT_MS, getStreamStallTimeoutMs, STREAM_STALL_TIMEOUT_MS } from '../config/timeouts';
import type { ConversationState } from './conversationTypes';

export const log = createLogger("ConversationStore");

// Re-export for slices
export { SCHEMA_VERSION, AI_OPENING_TIMEOUT_MS, CONVERSATION_TIMEOUT_MS, ANALYSIS_LISTENER_TIMEOUT_MS, ANALYSIS_STALL_TIMEOUT_MS, STREAM_STALL_TIMEOUT_MS, getStreamStallTimeoutMs };
export type { Socket, AnalyzedMessage, PronunciationResult, MessageStatus, ErrorInfo, ChatDirectives };
export type {
  TokenPayload,
  MessageSavedPayload,
  StreamErrorPayload,
  AnalysisCompletePayload,
  AnalysisFailedPayload,
  StreamCompletePayload,
  SessionTransitionPayload,
};

/**
 * Clear error state from all messages.
 * Used to auto-dismiss retry buttons when a new message is sent.
 * Clears both message-level errors (errorInfo) and analysis errors (analysisError).
 */
export function clearErrorMessages(messages: AnalyzedMessage[]): AnalyzedMessage[] {
  return messages
    .filter((m) => {
      // Remove error messages with empty content (artifacts of failed voice sends)
      if (m.status === "error") {
        const text = m.type === "voice" ? m.transcription : m.text;
        if (!text || text.trim().length === 0) return false;
      }
      return true;
    })
    .map((m) => {
      const hasError = m.status === "error";
      const hasAnalysisError = m.analysisError !== undefined;
      if (!hasError && !hasAnalysisError) return m;
      return {
        ...m,
        ...(hasError ? { status: "complete", errorInfo: undefined } : {}),
        ...(hasAnalysisError ? { analysisError: undefined } : {}),
      };
    });
}

/**
 * Sleep utility for retry delays
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Translate a stream/socket error code to a user-facing message.
 * Maps known backend codes to their i18n key in errors.api.*.
 * Falls back to the generic streaming error if the code is unknown.
 */

/**
 * Union of socket error codes the UI may need to translate — spans the
 * ack-stage codes carried on HandshakeErrorAck, the post-ack async codes
 * carried on op-specific error events, and the cross-cutting TTS/STT
 * codes. New codes go here; the `Record` below must stay exhaustive.
 */
type SocketErrorCode =
  | StreamErrorPayload['code']
  | AudioErrorPayload['code']
  | TranscriptionErrorPayload['code']
  // Ack-stage codes (HandshakeErrorAck) — listed explicitly because the
  // narrow per-event ack unions don't flow through `StreamErrorPayload`.
  | 'VALIDATION_FAILED'
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'SESSION_BUSY';

/** Error category from any socket error payload */
type SocketErrorCategory = StreamErrorPayload['category'];

const SOCKET_ERROR_I18N_KEY: Record<SocketErrorCode, string> = {
  VALIDATION_FAILED: 'errors.api.VALIDATION_FAILED',
  NOT_FOUND: 'errors.api.NOT_FOUND',
  FORBIDDEN: 'errors.api.FORBIDDEN',
  STREAM_ERROR: 'errors.api.INTERNAL_ERROR',
  INTERNAL_ERROR: 'errors.api.INTERNAL_ERROR',
  SESSION_BUSY: 'errors.api.SESSION_BUSY',
  AI_SERVICE_ERROR: 'errors.api.AI_SERVICE_ERROR',
  AI_PROVIDER_ERROR: 'errors.api.AI_PROVIDER_ERROR',
  AI_EMPTY_RESPONSE: 'errors.api.AI_EMPTY_RESPONSE',
  AI_STREAM_ERROR: 'errors.api.AI_STREAM_ERROR',
  TTS_ERROR: 'errors.api.INTERNAL_ERROR',
  STT_ERROR: 'errors.api.INTERNAL_ERROR',
};

/** Map socket error category to ErrorInfo type */
const CATEGORY_TO_ERROR_TYPE: Record<SocketErrorCategory, ErrorInfo['type']> = {
  VALIDATION: 'validation',
  BUSINESS: 'business',
  AUTH: 'auth',
  UNEXPECTED: 'server',
};

export function translateStreamError(code: SocketErrorCode, _rawMessage: string): string {
  const key = SOCKET_ERROR_I18N_KEY[code];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic key from SOCKET_ERROR_I18N_KEY map
  return i18n.t(key as any);
}

/**
 * Retry with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  baseDelayMs: number = 1000
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error: unknown) {
      lastError = error;

      if (attempt === maxAttempts) {
        throw error;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelayMs * Math.pow(2, attempt - 1);
      log.info("Retry attempt", { attempt, maxAttempts, delayMs: delay });
      await sleep(delay);
    }
  }

  throw lastError;
}

/**
 * Rebuild {@link ChatDirectives} from the raw `toolCalls` persisted on an
 * assistant message. Mirrors the backend's live-path directive dispatch
 * (`backend/src/machines/services.ts`): keep only directive-carrying tools and
 * strip the already-streamed `prose` arg, keying the remaining payload by tool
 * name.
 *
 * The GET-messages contract returns raw `toolCalls` but no parsed `directives`,
 * so without this a reloaded/resumed session loses the live-socket directive —
 * most importantly `pronunciation_assessment.reference_text`, whose absence
 * silently downgrades a scripted pronunciation drill to unscripted free-speech
 * STT (homophones mis-scored, phoneme feedback lost).
 *
 * Returns `undefined` when no directive tool calls are present, so callers can
 * spread it conditionally exactly like the live `stream_complete` handler.
 */
export function directivesFromToolCalls(
  toolCalls: ReadonlyArray<{ tool?: string; args?: unknown }> | null | undefined
): ChatDirectives | undefined {
  if (!toolCalls?.length) return undefined;
  const directives: Record<string, unknown> = {};
  for (const tc of toolCalls) {
    const name = tc?.tool;
    if (!name || !(DIRECTIVE_TOOL_NAMES as readonly string[]).includes(name)) {
      continue;
    }
    const args = tc.args;
    if (!args || typeof args !== "object") continue;
    // Strip `prose` — it was streamed as chat text, not a UI payload.
    const payload = { ...(args as Record<string, unknown>) };
    delete payload.prose;
    directives[name] = payload;
  }
  return Object.keys(directives).length > 0
    ? (directives as ChatDirectives)
    : undefined;
}

/**
 * Convert a server message to the AnalyzedMessage format used in the UI.
 *
 * @param options.expectAnalysis - If true and analysis is null/undefined on a
 *   user message, set `analysisError` so the AnalysisErrorBadge shows.
 *   Used for fresh send responses (HTTP path) where analysis was expected.
 *   NOT used when loading historical sessions (analysis may have been skipped).
 */
export function serverMessageToLocal(
  serverMsg: {
    id: string;
    role: "user" | "assistant";
    content?: string | null;
    toolCalls?: ReadonlyArray<{ tool?: string; args?: unknown; result?: unknown }> | null;
    analysis?: unknown;
    metadata?: unknown;
    createdAt: string;
  },
  sessionId: string,
  userId: string,
  options?: { expectAnalysis?: boolean }
): AnalyzedMessage {
  if (serverMsg.role === "assistant") {
    // Rehydrate directives from persisted toolCalls so resumed pronunciation
    // drills run scripted (reference_text) rather than falling back to free STT.
    const directives = directivesFromToolCalls(serverMsg.toolCalls);
    return {
      id: serverMsg.id,
      type: "text",
      text: serverMsg.content || '',
      userId,
      sessionId,
      createdAt: new Date(serverMsg.createdAt),
      status: "complete",
      role: "assistant",
      ...(directives ? { directives } : {}),
    };
  }

  // User message — may have pronunciation data
  const meta = serverMsg.metadata as Record<string, unknown> | null | undefined;
  const pronunciationData = meta?.pronunciationData as PronunciationResult | undefined;
  const analysis = (serverMsg.analysis || undefined) as ConversationAnalysis | undefined;

  // When analysis was expected but missing, set analysisError for retry badge
  const analysisError =
    options?.expectAnalysis && !analysis
      ? createAnalysisError()
      : undefined;

  if (pronunciationData) {
    return {
      id: serverMsg.id,
      type: "voice",
      transcription: serverMsg.content || '',
      pronunciationResult: pronunciationData,
      userId,
      sessionId,
      createdAt: new Date(serverMsg.createdAt),
      status: "complete",
      role: "user",
      analysis,
      isAnalysisExpanded: false,
      analysisError,
    };
  }

  return {
    id: serverMsg.id,
    type: "text",
    text: serverMsg.content || '',
    userId,
    sessionId,
    createdAt: new Date(serverMsg.createdAt),
    status: "complete",
    role: "user",
    analysis,
    isAnalysisExpanded: false,
    analysisError,
  };
}


// ── Stream text repair ─────────────────────────────────────────────────
// When 'done' arrives without 'stream_complete', the committed text may
// be truncated (tokens lost in transit). Fetch the authoritative text
// from the server and silently patch the message.

export function repairMessageText(
  sessionId: string,
  aiMessageId: string,
  set: (partial: Partial<ConversationState> | ((state: ConversationState) => Partial<ConversationState>)) => void,
): void {
  api.getSessionMessages(sessionId)
    .then((result) => {
      if (!result.ok) {
        log.warn("Failed to repair message text", { aiMessageId, kind: result.kind, error: result.error });
        return;
      }

      const serverMsg = result.data.messages.find(
        (m: { id: string }) => m.id === aiMessageId,
      );
      if (!serverMsg?.content) return;

      set((s) => {
        const localMsg = s.messages.find((m) => m.id === aiMessageId);
        if (!localMsg || localMsg.type !== 'text') return {};
        if (localMsg.text === serverMsg.content) return {};

        log.info("Repaired truncated message text", {
          aiMessageId,
          localLen: localMsg.text?.length ?? 0,
          serverLen: serverMsg.content?.length ?? 0,
        });
        return {
          messages: s.messages.map((m) =>
            m.id === aiMessageId && m.type === 'text'
              ? { ...m, text: serverMsg.content ?? '' }
              : m,
          ),
        };
      });
    })
    .catch((err) => {
      log.warn("Failed to repair message text (unexpected)", { aiMessageId, error: err });
    });
}

// ── Shared streaming core ──────────────────────────────────────────────
// Extracted from emitAndStreamMessage and retryConversation to avoid
// duplicating token buffering, directive parsing, and stream lifecycle.

interface StreamingCoreParams {
  socket: Socket;
  aiId: string;
  set: (partial: Partial<ConversationState> | ((state: ConversationState) => Partial<ConversationState>)) => void;
  /** Called when message_saved arrives — the handler differs between send and regenerate. */
  onMessageSaved: (data: MessageSavedPayload) => void;
  /** Called on stream_error — receives translated/formatted ErrorInfo. */
  onStreamError: (errorInfo: ErrorInfo, rawData: StreamErrorPayload) => void;
  /** Called when streaming completes successfully (after 'done'). */
  onComplete: () => void;
  /** Called when a session_transition event arrives during the stream. */
  onSessionTransition?: (data: SessionTransitionPayload) => void;
  /**
   * Called when 'done' arrives but 'stream_complete' was never received.
   * The caller should fetch the authoritative text from the server and patch
   * the message. Receives the AI message ID to repair.
   */
  onStreamTextMissing?: (aiMessageId: string) => void;
  /**
   * Optional "no proof of life" stall guard. When set, an internal timer
   * resets on every inbound event (token / message_saved / stream_complete
   * / stream_error / done / session_transition). If the timer elapses
   * with no event having arrived, `onStreamStalled` fires.
   *
   * Distinct from the caller's outer CONVERSATION_TIMEOUT_MS, which is
   * the worst-case upper bound for a legitimately-slow request. This
   * shorter heartbeat bounds the "user stares at a spinner with nothing
   * happening" UX — typically tuned for ~30s.
   *
   * Both `stallTimeoutMs` and `onStreamStalled` must be set together;
   * passing only one is a no-op (defensive: makes the call site
   * explicit about wanting the guard).
   */
  stallTimeoutMs?: number;
  onStreamStalled?: () => void;
}

interface StreamingCoreHandle {
  /** Call to unregister all listeners and flush token buffer. */
  cleanup: () => void;
  /**
   * Manually reset the stall timer. Use from listeners registered
   * *outside* setupStreamingCore (e.g. `analyzing` / `analysis_complete`
   * in the user-message path) that count as proof-of-life but aren't
   * part of the core's event set. No-op if no stall guard was
   * configured.
   */
  keepAlive: () => void;
  /**
   * True iff `stream_complete` has been processed for this streaming
   * session. Exposed so the parent `streamMessage` can guard against
   * late ack failures that arrive AFTER the stream has already
   * succeeded — see `logStaleStreamErrorAfterCommit` for the race
   * this is symmetric with.
   */
  isStreamTextCommitted: () => boolean;
}

/**
 * Log a "stale event after stream_complete" warning. Both
 * `handleStreamError` (in `setupStreamingCore`) and `onAckRejected`
 * (in `streamMessage`) need to drop late SESSION_BUSY notifications
 * that arrive AFTER a retry's stream has already succeeded — keeping
 * the rationale in one place so the two call sites don't drift.
 *
 * The race this guards against (per CONV-007 / Retry After Failure
 * regression history):
 *
 *   When the first send_message stream errored and the user taps
 *   retry, the backend's first-stream cleanup races the second-stream
 *   session-busy check. The retry's stream emits `stream_complete`
 *   successfully, then a SESSION_BUSY notification arrives milliseconds
 *   later for the same session. That notification can surface on EITHER
 *   path:
 *
 *     - As a `stream_error` socket event — caught by
 *       `handleStreamError` in `setupStreamingCore`.
 *     - As an `ok:false` ack on the original `regenerate_message`
 *       emit — caught by `onAckRejected` in `streamMessage`.
 *
 *   Without the guard, either path flips the AI bubble back to
 *   `status='error'`, the retry pill re-renders, and the test sees
 *   the retry as failed even though the stream actually delivered
 *   the text the user expected.
 *
 *   Two runs caught this on the wire so far:
 *     - 2026-06-07T00-23-31 at 01:30:18.392 — stream_complete at
 *       .391, stream_error code=SESSION_BUSY at .392 (caught by
 *       handleStreamError fix landed in this branch's earlier work).
 *     - 2026-06-09T06-47-17 at 1780988107004 — stream_complete at
 *       107004ms, ack rejected SESSION_BUSY at ~107018ms (caught by
 *       the onAckRejected guard added alongside this helper).
 *
 * Call site responsibility: each caller checks `streamTextCommitted`
 * itself BEFORE calling this helper, then dispatches the appropriate
 * bail-out (just-return for handleStreamError, resolve+return for
 * onAckRejected). The helper is just the shared "log it, here's why"
 * step — the actual behavioral guard varies enough that wrapping it
 * here would add ceremony without clarity.
 */
function logStaleStreamErrorAfterCommit(
  source: "stream_error" | "ack rejected",
  context: Record<string, unknown>,
): void {
  log.warn(
    `${source} after stream_complete — ignoring stale event`,
    context,
  );
}

/**
 * Set up the shared streaming listeners: token buffering, stream_complete
 * (with directive parsing), done, and error handling.
 *
 * Returns a handle for cleanup. The caller is responsible for emitting
 * the socket event and any additional listeners (e.g., analysis).
 */
export function setupStreamingCore(params: StreamingCoreParams): StreamingCoreHandle {
  const { socket, set: storeSet, onMessageSaved, onStreamError, onComplete } = params;
  let currentAiId = params.aiId;

  // ── Throttled token buffer ──────────────────────────────────
  let tokenBuffer = '';
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  const TOKEN_FLUSH_INTERVAL_MS = 50;

  const flushTokenBuffer = () => {
    flushTimer = null;
    if (!tokenBuffer) return;
    const chunk = tokenBuffer;
    tokenBuffer = '';
    storeSet((s) => ({
      streamingText: s.streamingText + chunk,
    }));
  };

  let streamTextCommitted = false;

  // ── Stall guard ─────────────────────────────────────────────
  // Optional "no proof of life" timer — resets on every inbound event.
  // The caller decides what to do on stall (typically: cleanup + show
  // a retryable error bubble). Wired here instead of at the call site
  // because every inbound stream event passes through this function,
  // so this is the single place that observes "proof of life".
  let stallTimer: ReturnType<typeof setTimeout> | null = null;
  const stallEnabled =
    typeof params.stallTimeoutMs === "number" &&
    params.stallTimeoutMs > 0 &&
    typeof params.onStreamStalled === "function";

  const clearStallTimer = () => {
    if (stallTimer) {
      clearTimeout(stallTimer);
      stallTimer = null;
    }
  };
  const armStallTimer = () => {
    if (!stallEnabled) return;
    clearStallTimer();
    stallTimer = setTimeout(() => {
      stallTimer = null;
      // Note: we don't call cleanup() here — the caller's onStreamStalled
      // is responsible for the cleanup + state mutation, so the call
      // site stays in control of how the stall is surfaced.
      params.onStreamStalled?.();
    }, params.stallTimeoutMs);
  };

  // All op-specific async error events share the same `AsyncErrorPayload`
  // shape, so one handler services them all — the spec splits event
  // names for clarity, not payload divergence.
  const OP_ERROR_EVENTS = [
    'send_message_error',
    'start_session_error',
    'regenerate_message_error',
    'send_audio_error',
  ] as const;

  const cleanup = () => {
    offServerEvent(socket, "token", onToken);
    offServerEvent(socket, "message_saved", handleMessageSaved);
    offServerEvent(socket, "stream_complete", handleStreamComplete);
    for (const name of OP_ERROR_EVENTS) {
      offServerEvent(socket, name, handleStreamError);
    }
    offServerEvent(socket, "done", handleDone);
    offServerEvent(socket, "session_transition", handleSessionTransition);
    if (flushTimer) clearTimeout(flushTimer);
    clearStallTimer();
    flushTokenBuffer();
  };

  const onToken = (data: TokenPayload) => {
    armStallTimer();
    tokenBuffer += data.text;
    if (!flushTimer) {
      flushTimer = setTimeout(flushTokenBuffer, TOKEN_FLUSH_INTERVAL_MS);
    }
    useStreamingTTSStore.getState().feedToken(data.text);
  };

  const handleMessageSaved = (data: MessageSavedPayload) => {
    armStallTimer();
    if (data.aiMessageId) currentAiId = data.aiMessageId;
    onMessageSaved(data);
  };

  const handleStreamComplete = (data: StreamCompletePayload) => {
    // Terminal event for text — clear stall guard. `done` may still
    // arrive afterward but the user has the final text already.
    clearStallTimer();
    if (streamTextCommitted) return;
    streamTextCommitted = true;
    if (flushTimer) clearTimeout(flushTimer);
    flushTokenBuffer();
    useStreamingTTSStore.getState().finishStreaming();

    const directives = data.directives as ChatDirectives | undefined;

    const finalText = data.text ?? '';

    storeSet((s) => ({
      messages: s.messages.map((m) => {
        if ((m.id === currentAiId || m.role === "assistant") && m.status === "streaming") {
          return {
            ...m,
            text: finalText || s.streamingText || '',
            ...(directives ? { directives } : {}),
          };
        }
        return m;
      }),
      streamingText: '',
      streamingMessageId: null,
    }));
  };

  const handleStreamError = (data: StreamErrorPayload) => {
    // See `logStaleStreamErrorAfterCommit` for the SESSION_BUSY race
    // this guards against (CONV-007 retry path). Symmetric guard in
    // `onAckRejected` covers the ack-side surfacing of the same race.
    if (streamTextCommitted) {
      logStaleStreamErrorAfterCommit("stream_error", {
        code: data.code,
        category: data.category,
        currentAiId,
      });
      return;
    }

    cleanup();
    useStreamingTTSStore.getState().cancel();

    const errorInfo: ErrorInfo = {
      type: CATEGORY_TO_ERROR_TYPE[data.category] || 'server',
      code: data.code,
      message: translateStreamError(data.code, data.message),
      retryable: data.category === 'UNEXPECTED',
    };

    storeSet((s) => ({
      messages: s.messages.map((m) => {
        if (m.id === currentAiId) {
          return {
            ...m,
            text: s.streamingText || '',
            status: "error" as MessageStatus,
            errorInfo,
          };
        }
        return m;
      }),
      isSendingMessage: false,
      isStreaming: false,
      streamingText: '',
      streamingMessageId: null,
    }));

    onStreamError(errorInfo, data);
  };

  const handleDone = () => {
    cleanup();
    const missedStreamComplete = !streamTextCommitted;
    if (missedStreamComplete) {
      useStreamingTTSStore.getState().finishStreaming();
    }

    storeSet((s) => ({
      messages: s.messages.map((m) => {
        if ((m.id === currentAiId || m.role === "assistant") && m.status === "streaming") {
          return {
            ...m,
            ...(streamTextCommitted ? {} : { text: s.streamingText }),
            status: "complete" as MessageStatus,
          };
        }
        return m;
      }),
      isStreaming: false,
      ...(!streamTextCommitted ? { streamingText: '', streamingMessageId: null } : {}),
    }));

    // stream_complete never arrived — the committed text may be truncated.
    // Ask the caller to fetch the authoritative text from the server.
    if (missedStreamComplete) {
      log.warn("stream_complete missed, requesting text repair", { aiMessageId: currentAiId });
      params.onStreamTextMissing?.(currentAiId);
    }

    AccessibilityInfo.announceForAccessibility?.("New response received");
    onComplete();
  };

  const handleSessionTransition = (data: SessionTransitionPayload) => {
    armStallTimer();
    params.onSessionTransition?.(data);
  };

  // Register core listeners
  onServerEvent(socket, "token", onToken);
  onServerEvent(socket, "message_saved", handleMessageSaved);
  onServerEvent(socket, "stream_complete", handleStreamComplete);
  for (const name of OP_ERROR_EVENTS) {
    onServerEvent(socket, name, handleStreamError);
  }
  onServerEvent(socket, "done", handleDone);
  onServerEvent(socket, "session_transition", handleSessionTransition);

  // Arm the stall guard immediately — the first inbound event must
  // arrive within the window or the caller is told the stream stalled.
  armStallTimer();

  return {
    cleanup,
    keepAlive: armStallTimer,
    isStreamTextCommitted: () => streamTextCommitted,
  };
}


/**
 * Unified streaming function for both sending new messages and regenerating.
 *
 * - mode 'send': emits send_message, tracks user message ID updates, runs analysis
 * - mode 'regenerate': emits regenerate_message, no analysis, no user message tracking
 *
 * Resolves when the conversation is complete (done/error).
 */
export type StreamMessageParams = {
  socket: Socket;
  sessionId: string;
  aiId: string;
  set: (partial: Partial<ConversationState> | ((state: ConversationState) => Partial<ConversationState>)) => void;
  /** Reference to the store for cross-slice access (e.g., loadSession after transition). */
  getConversationStore: () => { getState: () => ConversationState };
} & (
  | {
      mode: 'send';
      text: string;
      userMessageId: string;
      pronunciationData?: PronunciationResult;
      hasAnalysis: boolean;
      span: { setAttribute(key: string, value: number | string): void };
    }
  | {
      mode: 'regenerate';
      /** The user message ID to regenerate from */
      regenerateMessageId: string;
    }
);

export function streamMessage(params: StreamMessageParams): Promise<void> {
  const { socket, sessionId, aiId, set: storeSet, mode } = params;

  return new Promise<void>((resolve, reject) => {
    let currentUserId = mode === 'send' ? params.userMessageId : '';
    let analysisFailedDuringStream = false;

    // Forward-declared so analysis handlers can call `streamHandle.
    // keepAlive()` to count analysis events as proof-of-life for the
    // stall guard. Assigned just below by `setupStreamingCore`.
    let streamHandle: StreamingCoreHandle | null = null;

    // ── Analysis listeners (send mode only) ──────────────────────
    const hasAnalysis = mode === 'send' && params.hasAnalysis;
    let analysisTimeout: ReturnType<typeof setTimeout> | undefined;
    // "No proof of life" guard for the analysis pipeline — fires if
    // NEITHER `analyzing` nor a terminal analysis event lands within
    // ANALYSIS_STALL_TIMEOUT_MS (20s by default). Once `analyzing`
    // arrives we clear this; the longer ANALYSIS_LISTENER_TIMEOUT_MS
    // (75s) takes over for the in-progress case.
    let analysisStallTimeout: ReturnType<typeof setTimeout> | undefined;

    const analysisCleanup = () => {
      if (!hasAnalysis) return;
      offServerEvent(socket, "analyzing", onAnalyzing);
      offServerEvent(socket, "analysis_complete", onAnalysisComplete);
      offServerEvent(socket, "analysis_failed", onAnalysisFailed);
      clearTimeout(analysisTimeout);
      clearTimeout(analysisStallTimeout);
    };

    const onAnalyzing = () => {
      // Analysis events count as proof-of-life for the stall guard
      // even though they live outside setupStreamingCore.
      streamHandle?.keepAlive();
      // `analyzing` = backend confirmed analysis started → stall
      // guard is satisfied. The 75s upper bound (`analysisTimeout`)
      // still runs in case analysis_complete / analysis_failed never
      // land.
      clearTimeout(analysisStallTimeout);
      storeSet((s) => ({
        isAnalyzing: true,
        messages: s.messages.map((m) =>
          m.id === currentUserId && m.role === "user"
            ? { ...m, analysisPending: true }
            : m
        ),
      }));
    };

    const onAnalysisComplete = (data: AnalysisCompletePayload) => {
      streamHandle?.keepAlive();
      analysisCleanup();
      storeSet((s) => ({
        isAnalyzing: false,
        messages: s.messages.map((m) => {
          if (m.id === currentUserId && m.role === "user") {
            return {
              ...m,
              analysis: data.analysis as unknown as ConversationAnalysis | undefined,
              isAnalysisExpanded: false,
              analysisPending: false,
            };
          }
          return m;
        }),
      }));
      if (mode === 'send') {
        params.span.setAttribute("analysis.grammarErrors", data.analysis?.grammarErrors?.length || 0);
      }
    };

    const onAnalysisFailed = (data: AnalysisFailedPayload) => {
      streamHandle?.keepAlive();
      analysisCleanup();
      log.warn("Analysis failed during stream", { error: data.error, userMessageId: currentUserId });
      analysisFailedDuringStream = true;
      storeSet((s) => ({
        isAnalyzing: false,
        messages: s.messages.map((m) =>
          m.id === currentUserId && m.role === "user"
            ? { ...m, analysisPending: false }
            : m
        ),
      }));
    };

    if (hasAnalysis) {
      analysisTimeout = setTimeout(() => {
        analysisCleanup();
        log.warn("Analysis timed out", { userMessageId: currentUserId });
        analysisFailedDuringStream = true;
        storeSet((s) => {
          const msg = s.messages.find((m) => m.id === currentUserId);
          if (msg && msg.analysisPending && !msg.analysis) {
            return {
              isAnalyzing: false,
              messages: s.messages.map((m) =>
                m.id === currentUserId && m.role === "user"
                  ? { ...m, analysisPending: false }
                  : m
              ),
            };
          }
          return {};
        });
      }, ANALYSIS_LISTENER_TIMEOUT_MS);

      onServerEvent(socket, "analyzing", onAnalyzing);
      onServerEvent(socket, "analysis_complete", onAnalysisComplete);
      onServerEvent(socket, "analysis_failed", onAnalysisFailed);

      // Arm the analysis stall guard — fires if NO analysis event of
      // any kind arrives within ANALYSIS_STALL_TIMEOUT_MS, i.e. the
      // backend never even started the analysis pipeline. Once any
      // analysis event arrives (typically `analyzing` first), the
      // handlers clear this and the 75s upper bound takes over.
      //
      // On fire: log + tag to Sentry, mark analysis as failed-during-
      // stream so the AI bubble's onComplete sets analysisError on
      // the user message. We DON'T tear the chat down — the
      // conversation stream is independent and may still be flowing.
      analysisStallTimeout = setTimeout(() => {
        analysisCleanup();
        log.warn("Analysis stalled — no analyzing/complete/failed event within stall window", {
          userMessageId: currentUserId,
          _tags: { timeoutCode: 'ANALYSIS_STALLED', streamPath: 'analysis' },
        });
        analysisFailedDuringStream = true;
        storeSet((s) => ({
          isAnalyzing: false,
          messages: s.messages.map((m) =>
            m.id === currentUserId && m.role === "user"
              ? { ...m, analysisPending: false }
              : m
          ),
        }));
      }, ANALYSIS_STALL_TIMEOUT_MS);
    }

    // ── Streaming core ───────────────────────────────────────────
    // eslint-disable-next-line prefer-const -- declared here, assigned conditionally below
    let conversationTimeout: ReturnType<typeof setTimeout> | undefined;
    let pendingTransition: SessionTransitionPayload | null = null;

    // Both timeouts converge on the same retryable error bubble.
    // Pulled out so the stall guard and the long upper-bound can share
    // the exact same mutation — keeps the user-facing UX identical
    // regardless of which trigger fires first.
    const surfaceTimeout = (code: 'CONVERSATION_TIMEOUT' | 'STREAM_STALLED', logMessage: string) => {
      clearTimeout(conversationTimeout);
      streamHandle?.cleanup();
      analysisCleanup();
      log.error(logMessage, new Error(code), {
        sessionId,
        mode,
        // Sentry tags — surface as filterable facets so production
        // distribution ("which timeout fires most often?") is one
        // dashboard query, not a log scan. See iteration (B) in
        // docs/architecture/E2E_STREAM_STALL_GUARD.md (TBD).
        _tags: { timeoutCode: code, streamPath: mode },
      });
      const timeoutError: ErrorInfo = {
        type: 'network',
        code,
        // Client-side timeout — not a backend socket error code; translate directly.
        message: i18n.t('errors.api.TIMEOUT', { defaultValue: 'Request timed out' }),
        retryable: true,
      };
      storeSet((s) => ({
        messages: s.messages.map((m) => {
          if (m.id === aiId) return { ...m, text: s.streamingText || '', status: 'error' as MessageStatus, errorInfo: timeoutError };
          if (mode === 'send' && m.id === currentUserId) return { ...m, status: 'complete' as MessageStatus, analysisPending: false };
          return m;
        }),
        isSendingMessage: false,
        isStreaming: false,
        isAnalyzing: false,
        streamingText: '',
        streamingMessageId: null,
      }));
      reject(new Error(code));
    };

    streamHandle = setupStreamingCore({
      socket,
      aiId,
      set: storeSet,
      onMessageSaved: (data) => {
        if (mode === 'send') {
          const prevUserId = currentUserId;
          currentUserId = data.userMessageId;
          storeSet((s) => ({
            messages: s.messages.map((m) => {
              if (m.id === prevUserId) {
                return { ...m, id: data.userMessageId, status: "complete" as MessageStatus };
              }
              return m;
            }),
            isSendingMessage: false,
          }));
        }
        // regenerate: AI ID sync is handled by setupStreamingCore internally
      },
      onStreamError: (_errorInfo, rawData) => {
        clearTimeout(conversationTimeout);
        analysisCleanup();
        log.error("Stream error", new Error(rawData.message || rawData.code || 'Unknown stream error'), rawData);
        if (mode === 'send') {
          storeSet((s) => ({
            messages: s.messages.map((m) =>
              m.id === currentUserId
                ? { ...m, status: "complete" as MessageStatus, analysisPending: false }
                : m
            ),
          }));
        }
        if (mode === 'regenerate') {
          AccessibilityInfo.announceForAccessibility?.("Retry failed");
        }
        reject(new Error(rawData.message));
      },
      onSessionTransition: (data) => {
        log.info('Session transition received', { newSessionId: data.newSessionId, flowType: data.flowType });
        pendingTransition = data;
      },
      onStreamTextMissing: (aiMessageId) => {
        repairMessageText(sessionId, aiMessageId, storeSet);
      },
      onComplete: () => {
        clearTimeout(conversationTimeout);
        if (analysisFailedDuringStream) {
          storeSet((s) => ({
            messages: s.messages.map((m) =>
              m.id === currentUserId && m.role === "user" && !m.analysis
                ? { ...m, analysisError: createAnalysisError() }
                : m
            ),
          }));
        }

        // Navigate to new session after transition
        if (pendingTransition) {
          const { newSessionId } = pendingTransition;
          pendingTransition = null;
          log.info('Navigating to transitioned session', { newSessionId });
          // Short delay to let the UI settle before loading the new session
          setTimeout(() => {
            params.getConversationStore().getState().loadSession(newSessionId);
          }, 300);
        }

        resolve();
      },
      // Short "no proof of life" guard — bounds the spinner-with-no-
      // feedback window at ~30s. Analysis events (`analyzing`,
      // `analysis_complete`, `analysis_failed`) are registered outside
      // setupStreamingCore but call `streamHandle.keepAlive()` to
      // count as proof-of-life — see the analysis handlers above.
      // Read via getter so dev.override-stall-timeout (used by the
      // STALL-001/002/003 E2E stories) can lower it to ~3s for tests.
      stallTimeoutMs: getStreamStallTimeoutMs(),
      onStreamStalled: () => {
        surfaceTimeout('STREAM_STALLED', 'Conversation stalled — no events within stall window');
      },
    });

    // ── Emit ─────────────────────────────────────────────────────
    const onAckRejected = (error: string) => {
      // See `logStaleStreamErrorAfterCommit` for the SESSION_BUSY race
      // this guards against (CONV-007 retry path). Symmetric to the
      // guard in `handleStreamError` for the stream_error path; the
      // backend's late SESSION_BUSY can surface on either side. When
      // the stream already succeeded, treat the late ack failure as
      // a no-op and resolve cleanly.
      if (streamHandle?.isStreamTextCommitted()) {
        logStaleStreamErrorAfterCommit("ack rejected", {
          error,
          mode,
          aiId,
        });
        clearTimeout(conversationTimeout);
        resolve();
        return;
      }

      clearTimeout(conversationTimeout);
      streamHandle?.cleanup();
      analysisCleanup();
      const errorInfo: ErrorInfo = { type: "server" as const, message: error, retryable: true };
      storeSet((s) => ({
        messages: s.messages.map((m) => {
          if (m.id === aiId) return { ...m, status: "error" as MessageStatus, errorInfo };
          if (mode === 'send' && m.id === params.userMessageId) return { ...m, status: "complete" as MessageStatus, analysisPending: false };
          return m;
        }),
        isSendingMessage: false,
        isStreaming: false,
        streamingText: '',
        streamingMessageId: null,
      }));
      if (mode === 'regenerate') {
        AccessibilityInfo.announceForAccessibility?.("Retry failed");
      }
      reject(new Error(error));
    };

    if (mode === 'send') {
      emitEvent(socket, "send_message", {
        v: SCHEMA_VERSION,
        sessionId,
        text: params.text,
        userMessageId: params.userMessageId,
        ...(params.pronunciationData ? {
          pronunciationData: params.pronunciationData,
          pronunciationRaw: params.pronunciationData.rawJson,
        } : {}),
      }, (ackResponse: SendMessageAck) => {
        if (ackResponse && !ackResponse.ok) {
          log.error("Server rejected message", {
            code: ackResponse.code,
            message: ackResponse.message,
            category: ackResponse.category,
          });
          onAckRejected(ackResponse.message || "Message rejected");
        }
      });
    } else {
      emitEvent(socket, "regenerate_message", {
        v: SCHEMA_VERSION,
        sessionId,
        messageId: params.regenerateMessageId,
      }, (ackResponse: RegenerateMessageAck) => {
        if (ackResponse && !ackResponse.ok) {
          log.error("Server rejected regenerate", {
            code: ackResponse.code,
            message: ackResponse.message,
            category: ackResponse.category,
          });
          onAckRejected(ackResponse.message || "Regeneration rejected");
        }
      });
    }

    // Master timeout — fires if neither `done` nor `stream_error` arrives.
    // The stall guard above (30s) catches most "backend silent" failures
    // before this triggers; this only matters when events ARE arriving
    // (so the stall guard keeps resetting) but the stream never
    // completes within the worst-case pipeline budget.
    conversationTimeout = setTimeout(() => {
      surfaceTimeout('CONVERSATION_TIMEOUT', 'Conversation timed out');
    }, CONVERSATION_TIMEOUT_MS);
  });
}


// ── Session room helpers ─────────────────────────────────────────
// Emit join/leave to keep the socket in the correct session room.

export function joinSessionRoom(sessionId: string): void {
  const socket = getSocket();
  if (socket) {
    emitEvent(socket, 'join_session', { v: SCHEMA_VERSION, sessionId });
  }
}

export function leaveSessionRoom(sessionId: string): void {
  const socket = getSocket();
  if (socket) {
    emitEvent(socket, 'leave_session', { v: SCHEMA_VERSION, sessionId });
  }
}
